import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Notification } from '../../types/Notification.types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

export const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    fetchNotificationsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchNotificationsSuccess: (
      state,
      action: PayloadAction<{ notifications: Notification[]; unreadCount: number }>
    ) => {
      state.notifications = action.payload.notifications;
      state.unreadCount = action.payload.unreadCount;
      state.loading = false;
      state.error = null;
    },
    fetchNotificationsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    markNotificationAsReadStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    markNotificationAsReadSuccess: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload);
      if (notification && notification.isUnread) {
        notification.isUnread = false;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.loading = false;
      state.error = null;
    },
    markNotificationAsReadFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    markAllNotificationsAsReadStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    markAllNotificationsAsReadSuccess: (state) => {
      state.notifications = state.notifications.map((n) => ({
        ...n,
        isUnread: false,
      }));
      state.unreadCount = 0;
      state.loading = false;
      state.error = null;
    },
    markAllNotificationsAsReadFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  fetchNotificationsStart,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  markNotificationAsReadStart,
  markNotificationAsReadSuccess,
  markNotificationAsReadFailure,
  markAllNotificationsAsReadStart,
  markAllNotificationsAsReadSuccess,
  markAllNotificationsAsReadFailure,
  clearError,
} = notificationSlice.actions;

export default notificationSlice.reducer;

